import useComment from "./useComment.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import Login from "components/Structure/Login/Login.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import stripHtmlTags from "utils/trimHtmlTagsFunc.ts";
import Recaptcha from "components/Reusable/Recaptcha/Recaptcha.tsx";

import { useEffect, useRef, useState } from "react";
import { useUser } from "contexts/userContext/userContext.js";
import { useComments } from "contexts/commentsContext/commentsContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { awaiter } from "components/Pages/Home/Helpers/helpers.js";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts.ts";
import { useToasts } from "react-toast-notifications";

const CommentForm = ({
  edit, //data[id]
  reply, //data[id]
  callback,
  itemId,
}) => {
  const { setModal } = useModal();
  const { addToast } = useToasts();
  const { getAllCommentsData, addComment, editComment } = useComments();
  const { isLoggedIn } = useUser();
  const { getTextByKey, getBasicSettingsData } = useContent();
  const initialNewCommentData = {
    id: edit.Id, // if comment editing
    reply: reply.Id,
    content: edit.Content,
  };

  const { newCommentData, setNewCommentData, handleSubmit } = useComment(
    initialNewCommentData,
    getAllCommentsData(),
    addComment,
    editComment,
    itemId
  );

  const [isLoading, setIsLoading] = useState(false);

  const recaptchaRef = useRef();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const textRef = useRef();

  useEffect(() => {
    if (textRef.current) {
      const newHeight =
        (textRef.current.scrollHeight < 54 || textRef.current.value === ""
          ? 44
          : textRef.current.scrollHeight) + "px";
      textRef.current.style.height = "auto";
      textRef.current.style.height = newHeight;
      textRef.current.style.overflow =
        textRef.current.scrollHeight > 100 ? "auto" : "hidden";
    }
  }, [newCommentData.content]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);

    setCaptchaRequired(true);

    if (getBasicSettingsData().recaptchaType === "google" && !captchaToken) {
      await awaiter(RECAPTCHA_APPEAR_DELAY);
      await recaptchaRef.current.executeAsync();

      return;
    }

    if (!captchaToken) {
      addToast(getTextByKey("recaptcha-verification-error"), {
        appearance: "error",
      });
      return;
    }
    await handleSubmit(captchaToken);

    callback(null);
    setIsLoading(false);
  };

  useEffect(() => {
    if (captchaToken) {
      onSubmit();

      setCaptchaToken(null);
      setCaptchaRequired(false);
    }
  }, [captchaToken]);

  // useEffect(() => {
  //   textRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  // }, []);

  return (
    <div
      className={`comment-form-container ${
        !isLoggedIn() ? "column-direction" : ""
      }`}
    >
      {!isLoggedIn() ? (
        <div className="call-to-login">
          <a
            onClick={() =>
              setModal({
                modal: Login,
                modalSize: "full-modal",
              })
            }
          >
            {getTextByKey("login")}
          </a>{" "}
          {getTextByKey("call-to-action-to-add-comment")}
        </div>
      ) : null}
      <form onSubmit={onSubmit}>
        <textarea
          ref={textRef}
          className="form-field-input"
          value={stripHtmlTags(newCommentData.content)}
          placeholder={getTextByKey("placeholder-add-your-comment")}
          disabled={isLoggedIn() ? false : true}
          onChange={(e) =>
            setNewCommentData({ ...newCommentData, content: e.target.value })
          }
        />
        {captchaRequired ? (
          <Recaptcha
            recaptchaRef={recaptchaRef}
            formName="add-comment"
            onSuccess={(token) => setCaptchaToken(token)}
            // provider="cloudflare"
          />
        ) : null}
        <button type="submit" className="add-comment-icon">
          <Tooltip
            id={"postCommentIcon"}
            tooltipTextKey="post-comment-tooltip-text"
            top
          >
            {isLoading ? (
              <LoadingImageElement />
            ) : (
              <Icon name="send-comment" white />
            )}
          </Tooltip>
        </button>
      </form>
      {(edit.Id || reply.Id) && (
        <Icon name="x" onClick={() => callback(null)} gray />
      )}
    </div>
  );
};

CommentForm.defaultProps = {
  edit: {
    Id: null,
    Content: "",
  },
  reply: {
    Id: null,
  },
  callback: () => {
    return null;
  },
};

export default CommentForm;
